/**
 * Progressive lazyload: discovers section order from the lazyload page,
 * then fetches each section via Section Rendering API when it nears the viewport.
 */
(function () {
  const ROOT_MARGIN = "250px 0px";
  const MAX_CONCURRENT = 1;
  const MIN_MS_BETWEEN_LOADS = 120;
  const HOOKS_IDLE_TIMEOUT = 800;
  const PREFIX_SCAN_LIMIT = 65536;

  const STORAGE_KEY = "lazyload:tpl-prefix";

  const state = {
    manifest: [],
    pagePath: "pages/lazyload",
    templatePrefix: "",
    prefixPromise: null,
    root: null,
    stream: null,
    loaded: new Set(),
    loading: new Set(),
    active: 0,
    queue: [],
    slotObserver: null,
    started: false,
    lastLoadFinishedAt: 0,
    showShimmer: true,
    scrollLockBySlot: new Map(),
  };

  const SCROLL_LOCK_THRESHOLD = 40;

  function trackScrollLock(slot) {
    state.scrollLockBySlot.set(slot, {
      x: window.scrollX,
      y: window.scrollY,
    });
  }

  function restoreScrollLockIfIdle(slot) {
    const locked = state.scrollLockBySlot.get(slot);
    if (!locked) return;

    if (Math.abs(window.scrollY - locked.y) > SCROLL_LOCK_THRESHOLD) return;
    if (Math.abs(window.scrollX - locked.x) > SCROLL_LOCK_THRESHOLD) return;

    window.scrollTo(locked.x, locked.y);
  }

  function swapSlotContent(slot, mutate) {
    mutate();
    restoreScrollLockIfIdle(slot);
    requestAnimationFrame(function () {
      restoreScrollLockIfIdle(slot);
    });
  }

  function getShopifyRoot() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    return root.endsWith("/") ? root : root + "/";
  }

  function getPagePath() {
    const sentinel = document.querySelector("[data-lazyload-url]");
    const configured = sentinel && sentinel.getAttribute("data-lazyload-url");
    if (!configured) return "pages/lazyload";
    try {
      return new URL(configured, window.location.origin).pathname.replace(
        /^\//,
        "",
      );
    } catch (e) {
      return configured.replace(/^\//, "");
    }
  }

  function shortSectionId(rawId) {
    if (!rawId) return "";
    const id = String(rawId).replace(/^shopify-section-/, "");
    const match = id.match(/^template--\d+__(.+)$/);
    return match ? match[1] : id;
  }

  function discoverSectionIdsFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const selectors = [
      ".lazyload-content [id^='shopify-section-']",
      "#MainContent [id^='shopify-section-']",
      "main [id^='shopify-section-']",
    ];

    for (let i = 0; i < selectors.length; i += 1) {
      const nodes = doc.querySelectorAll(selectors[i]);
      if (!nodes.length) continue;

      const ids = [];
      const seen = new Set();
      nodes.forEach(function (node) {
        const shortId = shortSectionId(node.id);
        if (!shortId || seen.has(shortId)) return;
        seen.add(shortId);
        ids.push(shortId);
      });
      if (ids.length) return ids;
    }

    return [];
  }

  async function discoverManifest() {
    const pageUrl = getShopifyRoot() + state.pagePath.replace(/^\//, "");
    const res = await fetch(pageUrl);
    if (!res.ok) throw new Error("HTTP " + res.status);

    const html = await res.text();
    const sectionIds = discoverSectionIdsFromHtml(html);
    if (!sectionIds.length) {
      console.warn("[lazyload] No sections found on lazyload page");
    }
    return sectionIds;
  }

  function getShimmerTemplate() {
    const template = document.getElementById("lazyload-slot-shimmer-template");
    return template && template.content ? template.content : null;
  }

  function buildSlots(sectionIds) {
    if (!state.stream) return;

    const shimmerTemplate = getShimmerTemplate();

    sectionIds.forEach(function (sectionId, index) {
      const slot = document.createElement("div");
      slot.className = "lazyload-section-slot";
      slot.dataset.sectionId = sectionId;
      slot.dataset.lazyloadIndex = String(index);

      if (state.showShimmer && shimmerTemplate) {
        slot.appendChild(shimmerTemplate.cloneNode(true));
      }

      state.stream.appendChild(slot);
    });
  }

  function getTemplatePrefix() {
    if (state.templatePrefix) return state.templatePrefix;

    const fromDom = state.root && state.root.dataset.templatePrefix;
    if (fromDom) {
      state.templatePrefix = fromDom;
      return fromDom;
    }

    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        state.templatePrefix = cached;
        return cached;
      }
    } catch (e) {}

    return "";
  }

  function cacheTemplatePrefix(prefix) {
    if (!prefix) return;
    state.templatePrefix = prefix;
    try {
      sessionStorage.setItem(STORAGE_KEY, prefix);
    } catch (e) {}
  }

  function toApiSectionId(shortId) {
    if (!shortId) return shortId;
    if (shortId.indexOf("template--") === 0) return shortId;
    const prefix = getTemplatePrefix();
    return prefix ? prefix + "__" + shortId : shortId;
  }

  function extractSectionHtml(data, apiSectionId, shortId) {
    if (!data || typeof data !== "object") return null;

    const candidates = [apiSectionId, shortId];
    for (let i = 0; i < candidates.length; i += 1) {
      const key = candidates[i];
      if (!key || !Object.prototype.hasOwnProperty.call(data, key)) continue;
      const value = data[key];
      if (value && String(value).trim()) return String(value);
      if (value === null) return null;
    }

    const suffix = "__" + shortId;
    const matchedKey = Object.keys(data).find(function (key) {
      return key === shortId || key.endsWith(suffix);
    });
    if (!matchedKey) return null;

    const matchedValue = data[matchedKey];
    if (matchedValue && String(matchedValue).trim()) return String(matchedValue);
    return matchedValue === null ? null : null;
  }

  function extractPrefixFromHtml(html) {
    if (!html) return "";
    const match = String(html).match(/shopify-section-(template--\d+)__/);
    return match ? match[1] : "";
  }

  function buildSectionUrl(sectionId) {
    const pagePath = state.pagePath.replace(/^\//, "").replace(/\/$/, "");
    return (
      getShopifyRoot() +
      pagePath +
      "?sections=" +
      encodeURIComponent(sectionId)
    );
  }

  async function discoverTemplatePrefixFromApi() {
    const probes = state.manifest.slice(0, 4);
    for (let i = 0; i < probes.length; i += 1) {
      const shortId = probes[i];
      try {
        const res = await fetch(buildSectionUrl(shortId));
        if (!res.ok) continue;
        const data = await res.json();
        const html = extractSectionHtml(data, shortId, shortId);
        const prefix = extractPrefixFromHtml(html);
        if (prefix) {
          cacheTemplatePrefix(prefix);
          return prefix;
        }
        const fullKey = Object.keys(data).find(function (key) {
          return key.indexOf("template--") === 0 && key.endsWith("__" + shortId);
        });
        if (fullKey) {
          cacheTemplatePrefix(fullKey.split("__")[0]);
          return state.templatePrefix;
        }
      } catch (e) {}
    }
    return "";
  }

  async function discoverTemplatePrefixFromHtml() {
    const pageUrl = getShopifyRoot() + state.pagePath.replace(/^\//, "");
    const res = await fetch(pageUrl);
    if (!res.ok) throw new Error("HTTP " + res.status);

    const reader = res.body && res.body.getReader ? res.body.getReader() : null;
    const pattern = /id=["']shopify-section-(template--\d+)__/;

    if (!reader) {
      const html = await res.text();
      const match = html.match(pattern);
      cacheTemplatePrefix(match ? match[1] : "");
      return state.templatePrefix;
    }

    const decoder = new TextDecoder();
    let scanned = "";

    while (scanned.length < PREFIX_SCAN_LIMIT) {
      const chunk = await reader.read();
      if (chunk.done) break;
      scanned += decoder.decode(chunk.value, { stream: true });
      const match = scanned.match(pattern);
      if (match) {
        try {
          await reader.cancel();
        } catch (e) {}
        cacheTemplatePrefix(match[1]);
        return state.templatePrefix;
      }
    }

    cacheTemplatePrefix("");
    return "";
  }

  async function discoverTemplatePrefix() {
    if (getTemplatePrefix()) return getTemplatePrefix();
    if (state.prefixPromise) return state.prefixPromise;

    state.prefixPromise = (async function () {
      const fromApi = await discoverTemplatePrefixFromApi();
      if (fromApi) return fromApi;
      return discoverTemplatePrefixFromHtml();
    })().finally(function () {
      state.prefixPromise = null;
    });

    return state.prefixPromise;
  }

  async function ensureTemplatePrefix() {
    if (getTemplatePrefix()) return getTemplatePrefix();
    return discoverTemplatePrefix();
  }

  function scheduleIdle(fn, timeout) {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(fn, { timeout: timeout || HOOKS_IDLE_TIMEOUT });
      return;
    }
    setTimeout(fn, 1);
  }

  function runSectionHooks(slot) {
    const sectionEl =
      slot.querySelector("[id^='shopify-section-']") || slot.firstElementChild;

    scheduleIdle(function () {
      if (sectionEl) {
        sectionEl.dispatchEvent(
          new CustomEvent("shopify:section:load", { bubbles: true }),
        );
      }

      document.dispatchEvent(
        new CustomEvent("lazyload:section-loaded", {
          bubbles: true,
          detail: { slot: slot, sectionEl: sectionEl },
        }),
      );
    });
  }

  function markCompleteIfDone() {
    if (state.loaded.size !== state.manifest.length) return;
    const host = document.querySelector(".lazy-loaded-sections");
    if (host) host.classList.add("fade-in");
    scheduleIdle(function () {
      document.dispatchEvent(
        new CustomEvent("lazyload:complete", { bubbles: true }),
      );
    }, 1200);
  }

  function waitBeforeNextLoad() {
    const elapsed = Date.now() - state.lastLoadFinishedAt;
    const delay = Math.max(0, MIN_MS_BETWEEN_LOADS - elapsed);
    return new Promise(function (resolve) {
      if (delay <= 0) {
        scheduleIdle(resolve, 200);
        return;
      }
      setTimeout(function () {
        scheduleIdle(resolve, 200);
      }, delay);
    });
  }

  function processQueue() {
    while (state.active < MAX_CONCURRENT && state.queue.length) {
      const job = state.queue.shift();
      if (!job) break;
      loadSection(job.sectionId, job.slot);
    }
  }

  function enqueue(sectionId, slot, priority) {
    if (!sectionId || !slot) return;
    if (state.loaded.has(sectionId) || state.loading.has(sectionId)) return;
    if (slot.classList.contains("is-loaded") || slot.classList.contains("is-loading")) {
      return;
    }

    const job = { sectionId: sectionId, slot: slot };
    if (priority) {
      state.queue.unshift(job);
    } else {
      state.queue.push(job);
    }
    processQueue();
  }

  async function loadSection(sectionId, slot) {
    if (state.loaded.has(sectionId) || state.loading.has(sectionId)) return;

    state.loading.add(sectionId);
    state.active += 1;
    slot.classList.add("is-loading");
    trackScrollLock(slot);

    try {
      await waitBeforeNextLoad();
      await ensureTemplatePrefix();

      const apiSectionId = toApiSectionId(sectionId);
      const res = await fetch(buildSectionUrl(apiSectionId));
      if (!res.ok) throw new Error("HTTP " + res.status);

      const data = await res.json();
      let html = extractSectionHtml(data, apiSectionId, sectionId);

      if (html === null && !getTemplatePrefix()) {
        await discoverTemplatePrefix();
        const retryApiId = toApiSectionId(sectionId);
        const retryRes = await fetch(buildSectionUrl(retryApiId));
        if (!retryRes.ok) throw new Error("HTTP " + retryRes.status);
        const retryData = await retryRes.json();
        html = extractSectionHtml(retryData, retryApiId, sectionId);
      }

      if (!html || !String(html).trim()) {
        swapSlotContent(slot, function () {
          slot.classList.add("is-empty");
          slot.innerHTML = "";
        });
        state.loaded.add(sectionId);
        return;
      }

      swapSlotContent(slot, function () {
        slot.innerHTML = html;
        slot.classList.add("is-loaded");
      });
      state.loaded.add(sectionId);

      if (state.slotObserver) {
        state.slotObserver.unobserve(slot);
      }

      runSectionHooks(slot);
    } catch (err) {
      console.error("[lazyload] Failed to load section:", sectionId, err);
      slot.classList.add("is-error");
      state.loaded.add(sectionId);
    } finally {
      slot.classList.remove("is-loading");
      state.scrollLockBySlot.delete(slot);
      state.loading.delete(sectionId);
      state.active -= 1;
      state.lastLoadFinishedAt = Date.now();
      markCompleteIfDone();
      processQueue();
    }
  }

  function observeSlots() {
    if (!("IntersectionObserver" in window)) {
      state.manifest.forEach(function (sectionId, index) {
        const slot = state.stream.querySelector(
          '[data-lazyload-index="' + index + '"]',
        );
        if (slot) enqueue(sectionId, slot, false);
      });
      return;
    }

    state.slotObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const slot = entry.target;
          const sectionId = slot.dataset.sectionId;
          enqueue(sectionId, slot, false);
        });
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0 },
    );

    state.stream
      .querySelectorAll(".lazyload-section-slot")
      .forEach(function (slot) {
        state.slotObserver.observe(slot);
      });
  }

  function start() {
    if (state.started || !state.root || !state.manifest.length) return;
    state.started = true;
    observeSlots();
  }

  async function prepare() {
    state.manifest = await discoverManifest();
    if (!state.manifest.length) return;

    buildSlots(state.manifest);

    if (window.navigator.userAgent.includes("Googlebot")) {
      start();
      return;
    }

    if (!("IntersectionObserver" in window)) {
      start();
      return;
    }

    const gate = new IntersectionObserver(
      function (entries, obs) {
        if (
          !entries.some(function (e) {
            return e.isIntersecting;
          })
        ) {
          return;
        }
        obs.disconnect();
        start();
      },
      { root: null, rootMargin: "200px 0px", threshold: 0 },
    );

    gate.observe(state.root);
  }

  function boot() {
    state.root = document.querySelector(".lazyload-root");
    if (!state.root) return;

    state.stream = state.root.querySelector(".lazyload-sections-stream");
    if (!state.stream) return;

    state.pagePath = getPagePath();
    state.showShimmer = state.root.dataset.showShimmer !== "false";

    if (state.root.dataset.templatePrefix) {
      cacheTemplatePrefix(state.root.dataset.templatePrefix);
    }

    prepare().catch(function (err) {
      console.error("[lazyload] Failed to initialize:", err);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
